import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BASE_URL_TOKEN, Product, SearchCriteria } from '@raptor-fe/core';

/**
 * API service for browsing catalog products from federation sources (GTX, TM, DVI).
 * Products are read-only catalog items that can be Part, Labor, or Package types.
 * This service provides catalog browsing capabilities for admin UI configuration.
 */
@Injectable({
    providedIn: 'root',
})
export class ProductApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = inject(BASE_URL_TOKEN);

    /**
     * Retrieves all products from the catalog.
     * Returns all available Part, Labor, and Package products from the federation source.
     */
    getAll(tenantId: string, branchId: string): Observable<Product[]> {
        return this.http.get<Product[]>(this.getUrl(tenantId, branchId));
    }

    /**
     * Retrieves a single product by ID.
     * Used to fetch detailed product information including nested Part/Labor/Package details.
     */
    getById(tenantId: string, branchId: string, id: string): Observable<Product> {
        return this.http.get<Product>(this.getUrl(tenantId, branchId, id));
    }

    /**
     * Searches for products using complex search criteria.
     * Supports filtering, pagination, and ordering of catalog products.
     * @param searchCriteria The search criteria with filters, pagination, and ordering
     */
    search(tenantId: string, branchId: string, searchCriteria: SearchCriteria): Observable<Product[]> {
        return this.http.post<Product[]>(this.getUrl(tenantId, branchId, 'search'), searchCriteria);
    }

    /**
     * Searches for products by keyword (convenience method).
     * Simpler alternative to search() for basic keyword-based searches.
     * @param searchTerm The keyword to search for in product names/descriptions
     */
    searchByTerm(tenantId: string, branchId: string, searchTerm: string): Observable<Product[]> {
        const params = new HttpParams().set('searchTerm', searchTerm);
        return this.http.get<Product[]>(this.getUrl(tenantId, branchId, 'search'), { params });
    }

    private getUrl(tenantId: string, branchId: string, path = ''): string {
        const base = `${this.baseUrl}v1/tenant/${tenantId}/branch/${branchId}/product`;
        return path ? `${base}/${path}` : base;
    }
}
